/**
 * "Request a Quote" — the CUSTOMER's own guided journey (2026-09-20).
 *
 * THIS IS A REPAIR, NOT A NEW IDEA. `QuoteRequestViewSet` has granted
 * `IsCustomer` on create since the self-serve flow was first sketched, and
 * `perform_create` never had a customer branch: it resolved the caller's
 * broker through `_resolve_broker_id`, which knows a tenant stamp, a
 * `BrokerEmployee` row and a `BrokerUser` row -- and a customer login is none
 * of them. So every customer create answered
 * `400 {"broker": "Unable to resolve broker for this user."}`. Measured on
 * production: 0 of 28 customer logins could resolve a broker and 0 of 46 quote
 * requests had ever been created by a customer, which means
 * `pages/customers/BuyPolicyPage.tsx` has been 400-ing its "Request a Quote"
 * button for every customer since it shipped. The server half is
 * `backend/tests/test_customer_self_serve_quote.py`.
 *
 * WHAT IS REUSED RATHER THAN REBUILT
 * ----------------------------------
 * * Stages 1-2 (type & product, insurers) read `GET /products/catalog/?type=`
 *   -- `CustomerCatalogView`, which already scopes itself to the caller's own
 *   `Customer` rows through `BrokerPolicyAccess`, so a customer only ever sees
 *   what their brokerage is actually approved to sell.
 * * The step chrome is `components/common/Stepper`, the same one the broker
 *   wizard uses.
 * * Stages 9-10 (compare, choose) are the customer's EXISTING read-only
 *   screens: `MyQuotesPage` -> `MyQuoteDetailPage` -> the broker's shared
 *   public link, whose `accept` action is where a customer's choice is
 *   recorded. This page links into them; it does not duplicate them.
 * * Stages 12-14 (Broker Admin Leads, verification, payment, issuance) are the
 *   BROKER's existing flow. A customer-created request lands in their Quote
 *   Requests inbox by itself (`apps/quotes/enquiries.base_querysets` selects
 *   exactly `created_by__role="customer"`, `is_shared=False`). The journey
 *   ENDS by handing off into that.
 *
 * The broker's own `QuoteWizardPage` is deliberately NOT reused. Its very
 * first field is `customer_id`, filled from a broker-only customer search, and
 * its vehicle/RTO steps read broker-only endpoints; a customer would hit 403s
 * three steps in. Same reasoning as MyQuotesPage being a separate page from
 * QuotesListPage rather than that page with buttons hidden.
 *
 * TWO HONESTY RULES, both previously shipped as real bugs here
 * -----------------------------------------------------------
 * 1. **Stage 8 never spins on a hope.** Only 2 of ~26 brokers hold working
 *    insurer credentials. The journey asks
 *    `GET /quotes/requests/pricing-capability/` BEFORE the customer reaches
 *    the end and says which path this request will take; after the create it
 *    repeats what the SERVER said happened (`pricing.status`), never what the
 *    UI hoped. No premium is rendered that the server did not persist.
 * 2. **Three states, never two.** Every fetch here distinguishes loading,
 *    failed and empty. A failed catalog fetch must not read as "your broker
 *    sells nothing".
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  Input,
  Select,
  StepActions,
  StepContent,
  Stepper,
  type Step,
} from '@/components/common';
import { PageHeader } from '@/components/layout';
import api from '@/services/api';
import { getErrorMessage } from '@/services';
import {
  BROKER_CHOICE_REQUIRED_CODE,
  JOURNEY_COVER_OPTIONS,
  JOURNEY_DOCUMENT_TYPES,
  JOURNEY_SOURCE,
  NO_BROKERAGE_CODE,
  customerQuoteJourney,
  journeyUploadRefusal,
  type BrokerPricingCapability,
  type JourneyAttachment,
  type JourneyAttachmentOutcome,
  type JourneyPricingReport,
  type QuoteJourneySubmission,
} from '@/services/customerQuoteJourney';
import { useAuthStore } from '@/store';
import { formatCurrency, titleCase } from '@/utils';

/** The floor when `/products/types/lookup/` is empty or refuses -- the same
 *  list the customer dashboard falls back to, so the two agree about what a
 *  customer may ask for. */
const BUILT_IN_TYPES = [
  { code: 'motor', name: 'Motor Insurance' },
  { code: 'health', name: 'Health Insurance' },
  { code: 'life', name: 'Life Insurance' },
  { code: 'travel', name: 'Travel Insurance' },
];

/** Our OWN marker, never a server code: the request went out and nothing came
 *  back -- a client timeout (the axios client allows 30 s, and a live-pricing
 *  create blocks on the insurer adapters in sequence), a dropped connection or
 *  a 504 from nginx. The row may well be committed and the broker already
 *  notified, so this must NOT be reported as a refusal. */
const NO_SERVER_REPLY = 'client_no_response';

/** B-02/C1. Motor is zone-rated on the proposer's PIN and the GoDigit payload
 *  builder REFUSES to build without one, so a motor request sent without it
 *  cannot be priced at all -- it dies in our own code after the customer has
 *  filled in seven steps. The public origination journey has always made this
 *  mandatory; this journey asked for it as an optional extra. Same rule, six
 *  digits, as `wizard/schema.ts`. */
const PIN_CODE_REGEX = /^\d{6}$/;

// The cover codes and the source live beside the create call in
// `services/customerQuoteJourney.ts`, where the vitest pins them to the model.
const COVER_OPTIONS = JOURNEY_COVER_OPTIONS;

const ADDON_OPTIONS = [
  { value: 'zero_dep', label: 'Zero depreciation' },
  { value: 'roadside', label: 'Roadside assistance' },
  { value: 'engine_protect', label: 'Engine protection' },
  { value: 'consumables', label: 'Consumables cover' },
  { value: 'pa_cover', label: 'Personal accident cover' },
];

const STEPS: Step[] = [
  { id: 'type', title: 'Cover', description: 'Type & product' },
  { id: 'insurers', title: 'Insurers', description: 'Who to ask' },
  { id: 'you', title: 'Your details', description: 'Proposer' },
  { id: 'details', title: 'Insurance details', description: 'What to cover' },
  { id: 'coverage', title: 'Coverage', description: 'Add-ons' },
  // STAGE 6. Optional by construction -- see DocumentsStep. A customer who
  // cannot or will not upload anything must still be able to finish, so this
  // step has no `canContinue` guard of its own.
  { id: 'documents', title: 'Documents', description: 'Optional' },
  { id: 'review', title: 'Review', description: 'Check & send' },
  { id: 'result', title: 'Request sent', description: 'What happens next' },
];

interface JourneyState {
  insuranceType: string;
  productName: string;
  insurerIds: string[];
  insurerNames: string[];
  brokerId: string;
  proposerName: string;
  proposerEmail: string;
  proposerPhone: string;
  proposerPincode: string;
  registrationNumber: string;
  sumInsured: string;
  coverType: string;
  addons: string[];
  notes: string;
}

const EMPTY: JourneyState = {
  insuranceType: 'motor',
  productName: '',
  insurerIds: [],
  insurerNames: [],
  brokerId: '',
  proposerName: '',
  proposerEmail: '',
  proposerPhone: '',
  proposerPincode: '',
  registrationNumber: '',
  sumInsured: '',
  coverType: '',
  addons: [],
  notes: '',
};

interface TypeLookupRow {
  code?: string;
  name?: string;
}

export function RequestQuotePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState<JourneyState>(EMPTY);
  const [result, setResult] = useState<
    { id: string; requestNumber: string; pricing: JourneyPricingReport } | null
  >(null);
  const [refusal, setRefusal] = useState<{ code?: string; detail?: string } | null>(null);
  /** STAGE 6. Chosen, NOT uploaded: nothing reaches the server until the
   *  request itself is created. That is what makes "Remove" work without a
   *  delete endpoint -- the customer deliberately has no DELETE on
   *  `/quotes/documents/` (removal stays broker-side). */
  const [attachments, setAttachments] = useState<JourneyAttachment[]>([]);
  const [outcomes, setOutcomes] = useState<JourneyAttachmentOutcome[]>([]);
  /** The PIN refusal is only shown once the customer has been near the field.
   *  Continue is disabled either way, and the helper text says why; red text
   *  on a field nobody has reached yet is a telling-off, not a hint. */
  const [pincodeTouched, setPincodeTouched] = useState(false);

  const set = <K extends keyof JourneyState>(key: K, value: JourneyState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // ---------------------------------------------------------------- lookups

  const typesQuery = useQuery<TypeLookupRow[]>({
    queryKey: ['insurance-types', 'lookup'],
    queryFn: async () => {
      const body = (await api.get('/products/types/lookup/')).data;
      return Array.isArray(body) ? body : (body?.results ?? []);
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const types = useMemo(() => {
    const configured = (typesQuery.data ?? []).filter((row) => row?.code);
    if (configured.length === 0) return BUILT_IN_TYPES;
    return configured.map((row) => ({
      code: String(row.code).toLowerCase(),
      name: row.name || titleCase(String(row.code)),
    }));
  }, [typesQuery.data]);

  /** STAGE 8 HONESTY, asked up front. Also the source of the brokerage list:
   *  the customer must never have one picked for them. */
  const capabilityQuery = useQuery({
    queryKey: ['customer-pricing-capability', form.insuranceType],
    queryFn: () => customerQuoteJourney.pricingCapability(form.insuranceType),
    retry: false,
  });

  const catalogQuery = useQuery({
    queryKey: ['customer-catalog', form.insuranceType],
    queryFn: () => customerQuoteJourney.catalog(form.insuranceType),
    retry: false,
  });

  /** B-02/C1. The customer's own record already holds the address the proposal
   *  would use, so asking them to retype the PIN is asking twice. Shares the
   *  'customer-me' key with the dashboard, My Contacts and BuyPolicyPage, so
   *  this is usually a cache hit rather than another request. A failure here is
   *  never fatal: the field is simply left for them to fill in. */
  const profileQuery = useQuery<{ pincode?: string | null }>({
    queryKey: ['customer-me'],
    queryFn: async () => (await api.get('/customers/me/')).data,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const brokerages = capabilityQuery.data?.brokers ?? [];
  const chosenBroker: BrokerPricingCapability | undefined =
    brokerages.length === 1
      ? brokerages[0]
      : brokerages.find((row) => row.broker_id === form.brokerId);

  // Prefill the proposer from the login, the way the broker wizard prefills
  // from the selected customer. Only ever fills a field the customer has not
  // typed in -- it must never overwrite an edit.
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      proposerName: prev.proposerName || user?.full_name || '',
      proposerEmail: prev.proposerEmail || user?.email || '',
      proposerPhone: prev.proposerPhone || user?.phone || '',
    }));
  }, [user?.full_name, user?.email, user?.phone]);

  // Same rule for the PIN, from the Customer record rather than the login --
  // `users.User` has no address, `customers.Customer` does. Separate effect
  // because it settles later than the auth store.
  //
  // `Customer.pincode` is free text typed by broker staff and nothing on that
  // path validates it, so what comes back is not necessarily a PIN. Digits are
  // kept ('411 001' is a PIN with a space in it); anything that still is not
  // six digits is NOT prefilled, because a field that looks answered and
  // silently fails the gate below is worse than an empty one -- the refusal
  // says so instead.
  const storedPincode = (profileQuery.data?.pincode || '').trim();
  const profilePincode = storedPincode.replace(/\D/g, '');
  const profilePincodeUsable = PIN_CODE_REGEX.test(profilePincode);
  useEffect(() => {
    if (!profilePincode || !profilePincodeUsable) return;
    setForm((prev) => (prev.proposerPincode ? prev : { ...prev, proposerPincode: profilePincode }));
  }, [profilePincode, profilePincodeUsable]);

  // ---------------------------------------------------------------- the write

  const submit = useMutation({
    mutationFn: async () => {
      const body: QuoteJourneySubmission = {
        insurance_type: form.insuranceType,
        business_type: 'new',
        proposer_type: 'individual',
        proposer_name: form.proposerName,
        proposer_email: form.proposerEmail || undefined,
        proposer_phone: form.proposerPhone || undefined,
        proposer_pincode: form.proposerPincode || undefined,
        registration_number: form.registrationNumber || undefined,
        sum_insured: form.sumInsured || undefined,
        cover_type: form.coverType || undefined,
        requested_addons: form.addons.length ? form.addons : undefined,
        source: JOURNEY_SOURCE,
        notes: form.notes || undefined,
        // The journey's own answers, kept where the broker pricing this by
        // hand will read them. Echoing them back on screen without persisting
        // them is this platform's signature defect.
        additional_data: {
          customer_journey: {
            preferred_insurer_ids: form.insurerIds,
            preferred_insurer_names: form.insurerNames,
            product_name: form.productName || undefined,
            cover_type: form.coverType || undefined,
            requested_addons: form.addons,
          },
        },
      };
      // Sent ONLY when there is a genuine choice. The server validates it
      // against this customer's own records and 403s otherwise.
      if (brokerages.length > 1 && form.brokerId) body.broker = form.brokerId;
      const created = await customerQuoteJourney.createQuoteRequest(body);
      // STAGE 6, and the ORDER matters. The documents are committed AFTER the
      // request exists, because they hang off its id -- and `uploadDocuments`
      // never throws, so a file that would not attach cannot turn a request
      // that WAS created into "your request was not sent".
      const attached = attachments.length
        ? await customerQuoteJourney.uploadDocuments(created.id, attachments)
        : [];
      return { created, attached };
    },
    onSuccess: ({ created, attached }) => {
      setRefusal(null);
      setOutcomes(attached);
      setResult({
        id: created.id,
        requestNumber: created.request_number,
        pricing: created.pricing,
      });
      setStepIndex(STEPS.length - 1);
    },
    onError: (error: unknown) => {
      const response = (error as { response?: { data?: { code?: string; detail?: string } } })
        ?.response;
      // NO reply at all is not a refusal, and saying "your request was not
      // sent" here is a claim we cannot make: ATOMIC_REQUESTS is off, so the
      // QuoteRequest row is committed -- and the broker notified -- before the
      // pricing round trip that blew the 30 s client timeout. Telling the
      // customer it failed is what makes them press Send again and put a
      // duplicate lead in the broker's inbox.
      if (!response) {
        setRefusal({ code: NO_SERVER_REPLY });
        return;
      }
      const body = response.data;
      setRefusal({ code: body?.code, detail: body?.detail || getErrorMessage(error) });
    },
  });

  /** The request is already filed; only the files that did not attach are
   *  sent again. Retrying them all would duplicate the ones that landed. */
  const retryUploads = useMutation({
    mutationFn: async () => {
      if (!result) return [] as JourneyAttachmentOutcome[];
      const failedIds = outcomes.filter((o) => o.status === 'failed').map((o) => o.attachmentId);
      const again = attachments.filter((a) => failedIds.includes(a.id));
      return customerQuoteJourney.uploadDocuments(result.id, again);
    },
    onSuccess: (fresh) => {
      setOutcomes((prev) =>
        prev.map((previous) => fresh.find((o) => o.attachmentId === previous.attachmentId) ?? previous)
      );
    },
  });

  // ---------------------------------------------------------------- guards

  // B-02/C1. Motor only: the PIN is what the insurer zone-rates on, and the
  // GoDigit builder raises without it, so letting a motor request through
  // without one buys a refusal seven steps later instead of a question now.
  // Every other type is priced by hand and does not need it.
  const pincodeRequired = form.insuranceType === 'motor';
  const pincodeValid = PIN_CODE_REGEX.test(form.proposerPincode.trim());
  // Normally the refusal waits until they have touched the field -- telling
  // someone off for an answer they have not reached yet is rude. The exception
  // is a record that HOLDS a PIN we could not use: then the field is empty
  // through no fault of theirs and Continue is disabled, so it has to say why
  // without waiting to be touched.
  const pincodeUnusableOnFile = Boolean(storedPincode) && !profilePincodeUsable;
  const pincodeRefusal =
    pincodeRequired && (pincodeTouched || pincodeUnusableOnFile) && !pincodeValid
      ? form.proposerPincode.trim()
        ? 'A PIN code is 6 digits.'
        : pincodeUnusableOnFile
          ? `The PIN code on your record ("${storedPincode}") is not a 6-digit PIN — please enter it.`
          : 'Your PIN code is needed to price motor cover.'
      : undefined;

  const canContinue = (() => {
    switch (STEPS[stepIndex]?.id) {
      case 'type':
        return Boolean(form.insuranceType) && (brokerages.length <= 1 || Boolean(form.brokerId));
      case 'you':
        return (
          form.proposerName.trim().length > 0 &&
          form.proposerPhone.trim().length > 0 &&
          (!pincodeRequired || pincodeValid)
        );
      default:
        return true;
    }
  })();

  const goNext = () => setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  // A login on no brokerage's books cannot raise a request at all, and saying
  // so up front is kinder than letting them fill in six steps first. 3 live
  // logins are in this state.
  const noBrokerage =
    capabilityQuery.isSuccess &&
    (capabilityQuery.data?.code === NO_BROKERAGE_CODE || brokerages.length === 0);

  return (
    <div>
      <PageHeader
        title="Request a Quote"
        subtitle="Tell your advisor what you need and they will come back with options"
        breadcrumbs={[{ label: 'My Quotes', href: '/my-quotes' }, { label: 'Request a Quote' }]}
      />

      <Card className="mb-6">
        <Stepper steps={STEPS} currentStep={stepIndex} />
      </Card>

      {capabilityQuery.isLoading ? (
        <Card>
          <div className="animate-pulse space-y-3" data-testid="journey-capability-skeleton">
            <div className="h-4 w-48 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-24 rounded bg-gray-100 dark:bg-gray-700" />
          </div>
        </Card>
      ) : capabilityQuery.isError ? (
        /* NOT "you have no brokerage". We do not know that -- we know we failed
           to ask. Three states, never two. */
        <Card>
          <EmptyState
            title="We could not reach your advisor's details"
            description="Nothing has been lost and nothing has been sent. Please try again."
            action={
              <Button onClick={() => capabilityQuery.refetch()}>Try again</Button>
            }
          />
        </Card>
      ) : noBrokerage ? (
        <Card>
          <EmptyState
            icon={<ExclamationTriangleIcon />}
            title="This login is not linked to a brokerage yet"
            description={
              capabilityQuery.data?.detail ||
              'Ask your advisor to add you as a customer, or use their website to request a quote.'
            }
            action={<Button onClick={() => navigate('/my-quotes')}>Back to My Quotes</Button>}
          />
        </Card>
      ) : (
        <Card>
          <StepContent>
            {STEPS[stepIndex]?.id === 'type' && (
              <TypeStep
                types={types}
                value={form.insuranceType}
                onChange={(code) => {
                  set('insuranceType', code);
                  set('insurerIds', []);
                  set('insurerNames', []);
                  set('productName', '');
                }}
                brokerages={brokerages}
                brokerId={form.brokerId}
                onBrokerChange={(id) => set('brokerId', id)}
              />
            )}

            {STEPS[stepIndex]?.id === 'insurers' && (
              <InsurerStep
                isLoading={catalogQuery.isLoading}
                isError={catalogQuery.isError}
                onRetry={() => catalogQuery.refetch()}
                insurers={catalogQuery.data?.insurers ?? []}
                selectedIds={form.insurerIds}
                onToggle={(id, name) => {
                  const has = form.insurerIds.includes(id);
                  set('insurerIds', has ? form.insurerIds.filter((x) => x !== id) : [...form.insurerIds, id]);
                  set(
                    'insurerNames',
                    has
                      ? form.insurerNames.filter((x) => x !== name)
                      : [...form.insurerNames, name]
                  );
                }}
                productName={form.productName}
                onProductChange={(name) => set('productName', name)}
              />
            )}

            {STEPS[stepIndex]?.id === 'you' && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  id="journey-proposer-name"
                  name="proposer_name"
                  label="Full name"
                  required
                  value={form.proposerName}
                  onChange={(e) => set('proposerName', e.target.value)}
                />
                <Input
                  id="journey-proposer-phone"
                  name="proposer_phone"
                  label="Mobile number"
                  required
                  value={form.proposerPhone}
                  onChange={(e) => set('proposerPhone', e.target.value)}
                />
                <Input
                  id="journey-proposer-email"
                  name="proposer_email"
                  label="Email"
                  value={form.proposerEmail}
                  onChange={(e) => set('proposerEmail', e.target.value)}
                />
                <Input
                  id="journey-proposer-pincode"
                  name="proposer_pincode"
                  label="PIN code"
                  required={pincodeRequired}
                  inputMode="numeric"
                  maxLength={6}
                  helperText={
                    pincodeRequired
                      ? 'Motor premiums are zone-rated on this, so the insurer will not quote without it.'
                      : 'Helps your advisor price this accurately.'
                  }
                  error={pincodeRefusal}
                  value={form.proposerPincode}
                  onBlur={() => setPincodeTouched(true)}
                  // Digits only: a PIN typed with a space reaches the server as
                  // a value no insurer matches, and nothing downstream trims it.
                  onChange={(e) => {
                    setPincodeTouched(true);
                    set('proposerPincode', e.target.value.replace(/\D/g, '').slice(0, 6));
                  }}
                />
              </div>
            )}

            {STEPS[stepIndex]?.id === 'details' && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {form.insuranceType === 'motor' ? (
                  <Input
                    id="journey-registration-number"
                    name="registration_number"
                    label="Vehicle registration number"
                    placeholder="MH01AB1234"
                    value={form.registrationNumber}
                    onChange={(e) => set('registrationNumber', e.target.value.toUpperCase())}
                  />
                ) : (
                  <Input
                    id="journey-sum-insured"
                    name="sum_insured"
                    label="Cover amount you want"
                    inputMode="numeric"
                    value={form.sumInsured}
                    // Digits only. "5,00,000" is how an Indian customer
                    // writes a lakh figure, and the server's decimal field
                    // 400s on the commas -- the whole request, not the field.
                    onChange={(e) => set('sumInsured', e.target.value.replace(/[^\d.]/g, ''))}
                  />
                )}
                <Input
                  id="journey-notes"
                  name="notes"
                  label="Anything your advisor should know"
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                />
              </div>
            )}

            {STEPS[stepIndex]?.id === 'coverage' && (
              <div className="space-y-4">
                <Select
                  id="journey-cover-type"
                  name="cover_type"
                  label="Cover type"
                  options={COVER_OPTIONS}
                  value={form.coverType}
                  onChange={(e) => set('coverType', e.target.value)}
                />
                <fieldset>
                  <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Add-ons you are interested in
                  </legend>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {ADDON_OPTIONS.map((addon) => (
                      <label key={addon.value} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.addons.includes(addon.value)}
                          onChange={() =>
                            set(
                              'addons',
                              form.addons.includes(addon.value)
                                ? form.addons.filter((a) => a !== addon.value)
                                : [...form.addons, addon.value]
                            )
                          }
                        />
                        {addon.label}
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>
            )}

            {STEPS[stepIndex]?.id === 'documents' && (
              <DocumentsStep
                attachments={attachments}
                onAdd={(attachment) => setAttachments((prev) => [...prev, attachment])}
                onRemove={(id) => setAttachments((prev) => prev.filter((a) => a.id !== id))}
                onSkip={goNext}
              />
            )}

            {STEPS[stepIndex]?.id === 'review' && (
              <ReviewStep
                form={form}
                attachments={attachments}
                brokerName={chosenBroker?.broker_name ?? ''}
                capability={chosenBroker}
                refusal={refusal}
                isSubmitting={submit.isPending}
                onChooseBroker={() => setStepIndex(0)}
              />
            )}

            {STEPS[stepIndex]?.id === 'result' && result && (
              <ResultStep
                result={result}
                brokerName={chosenBroker?.broker_name ?? ''}
                outcomes={outcomes}
                onRetryUploads={() => retryUploads.mutate()}
                isRetrying={retryUploads.isPending}
              />
            )}
          </StepContent>

          {STEPS[stepIndex]?.id !== 'result' && (
            <StepActions>
              <Button variant="outline" onClick={stepIndex === 0 ? () => navigate('/my-quotes') : goBack}>
                {stepIndex === 0 ? 'Cancel' : 'Back'}
              </Button>
              {STEPS[stepIndex]?.id === 'review' ? (
                <Button
                  onClick={() => submit.mutate()}
                  isLoading={submit.isPending}
                  disabled={submit.isPending}
                >
                  Send request
                </Button>
              ) : (
                <Button onClick={goNext} disabled={!canContinue}>
                  Continue
                </Button>
              )}
            </StepActions>
          )}
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function TypeStep({
  types,
  value,
  onChange,
  brokerages,
  brokerId,
  onBrokerChange,
}: {
  types: { code: string; name: string }[];
  value: string;
  onChange: (code: string) => void;
  brokerages: BrokerPricingCapability[];
  brokerId: string;
  onBrokerChange: (id: string) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          What do you want to insure?
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {types.map((type) => (
            <button
              key={type.code}
              type="button"
              aria-pressed={value === type.code}
              onClick={() => onChange(type.code)}
              className={`rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                value === type.code
                  ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                  : 'border-gray-300 text-gray-700 hover:border-gray-400 dark:border-gray-600 dark:text-gray-300'
              }`}
            >
              {type.name}
            </button>
          ))}
        </div>
      </div>

      {/* MORE THAN ONE BROKERAGE: the customer chooses, and nobody chooses for
          them. Filing a quote against the wrong brokerage puts it in the wrong
          book and earns the wrong house its commission, so the server refuses
          a create with no choice rather than taking the first. */}
      {brokerages.length > 1 && (
        <Select
          id="journey-broker"
          name="broker"
          label="Which of your advisors should handle this?"
          required
          placeholder="Choose a brokerage"
          options={brokerages.map((row) => ({ value: row.broker_id, label: row.broker_name }))}
          value={brokerId}
          onChange={(e) => onBrokerChange(e.target.value)}
        />
      )}

      {brokerages.length === 1 && (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          This request will go to <strong>{brokerages[0].broker_name}</strong>.
        </p>
      )}
    </div>
  );
}

function InsurerStep({
  isLoading,
  isError,
  onRetry,
  insurers,
  selectedIds,
  onToggle,
  productName,
  onProductChange,
}: {
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  insurers: {
    insurer_id: string;
    insurer_name: string;
    claim_settlement_ratio: number | null;
    products: { id: string; name: string }[];
  }[];
  selectedIds: string[];
  onToggle: (id: string, name: string) => void;
  productName: string;
  onProductChange: (name: string) => void;
}) {
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3" data-testid="journey-insurers-skeleton">
        <div className="h-16 rounded bg-gray-100 dark:bg-gray-700" />
        <div className="h-16 rounded bg-gray-100 dark:bg-gray-700" />
      </div>
    );
  }
  if (isError) {
    // A failed fetch is not "your broker sells nothing".
    return (
      <EmptyState
        title="We could not load the insurers"
        description="Your request has not been sent. Try again, or continue and let your advisor choose."
        action={<Button onClick={onRetry}>Try again</Button>}
      />
    );
  }
  if (insurers.length === 0) {
    return (
      <EmptyState
        title="No insurers listed for this cover yet"
        description="Continue anyway — your advisor will pick the insurers to approach."
      />
    );
  }

  const products = insurers.flatMap((i) => i.products.map((p) => ({ value: p.name, label: `${p.name} — ${i.insurer_name}` })));

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Choose the insurers you would like quoted. Leave them all unticked and your advisor
        decides.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {insurers.map((insurer) => (
          <label
            key={insurer.insurer_id}
            className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700"
          >
            <input
              type="checkbox"
              checked={selectedIds.includes(insurer.insurer_id)}
              onChange={() => onToggle(insurer.insurer_id, insurer.insurer_name)}
              aria-label={`Ask ${insurer.insurer_name}`}
            />
            <span>
              <span className="block text-sm font-medium text-gray-900 dark:text-white">
                {insurer.insurer_name}
              </span>
              {insurer.claim_settlement_ratio ? (
                <span className="block text-xs text-gray-500">
                  Claim settlement ratio: {insurer.claim_settlement_ratio}%
                </span>
              ) : null}
            </span>
          </label>
        ))}
      </div>
      {products.length > 0 && (
        <Select
          id="journey-product"
          name="product"
          label="A particular plan? (optional)"
          placeholder="No preference"
          options={[{ value: '', label: 'No preference' }, ...products]}
          value={productName}
          onChange={(e) => onProductChange(e.target.value)}
        />
      )}
    </div>
  );
}

function DocumentsStep({
  attachments,
  onAdd,
  onRemove,
  onSkip,
}: {
  attachments: JourneyAttachment[];
  onAdd: (attachment: JourneyAttachment) => void;
  onRemove: (id: string) => void;
  onSkip: () => void;
}) {
  const [documentType, setDocumentType] = useState(JOURNEY_DOCUMENT_TYPES[0].value);
  const [refusal, setRefusal] = useState<string | null>(null);
  const inputId = 'journey-document-file';

  const choose = (file: File | undefined) => {
    if (!file) return;
    // Told here rather than after a 10 MB upload has crossed a phone
    // connection. The server applies the same rule and is the authority.
    const problem = journeyUploadRefusal(file);
    if (problem) {
      setRefusal(`${file.name}: ${problem}`);
      return;
    }
    setRefusal(null);
    onAdd({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      documentType,
    });
  };

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-200">
        <p className="font-medium text-gray-900 dark:text-white">
          Anything that helps your advisor quote this? (optional)
        </p>
        <p className="mt-1">
          Your RC, last year&apos;s policy or an ID proof. You can send this request without
          attaching anything — nothing is uploaded until you press Send.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Select
          id="journey-document-type"
          name="document_type"
          label="What is this document?"
          options={JOURNEY_DOCUMENT_TYPES.map((row) => ({ value: row.value, label: row.label }))}
          value={documentType}
          onChange={(e) => setDocumentType(e.target.value)}
        />
        <div>
          <label
            htmlFor={inputId}
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Choose a file
          </label>
          <input
            id={inputId}
            name="file"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.tif,.tiff"
            className="block w-full text-sm text-gray-700 dark:text-gray-200"
            onChange={(e) => {
              choose(e.target.files?.[0]);
              // Let the same file be picked again after a removal.
              e.target.value = '';
            }}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">PDF or photo, up to 10 MB each.</p>
        </div>
      </div>

      {refusal && (
        <p className="text-sm text-red-700 dark:text-red-300" role="alert">
          {refusal}
        </p>
      )}

      {/* Three states, and "none chosen" is one of them -- stated, not implied
          by an empty box. Nothing here has been sent, so none of it claims to
          have been. */}
      {attachments.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="journey-documents-none">
          No documents chosen. That is fine — you can send the request without any.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700" data-testid="journey-documents-list">
          {attachments.map((attachment) => (
            <li key={attachment.id} className="flex items-center justify-between gap-4 py-2 text-sm">
              <span className="min-w-0">
                <span className="block truncate font-medium text-gray-900 dark:text-white">
                  {attachment.file.name}
                </span>
                <span className="block text-xs text-gray-500">
                  {JOURNEY_DOCUMENT_TYPES.find((t) => t.value === attachment.documentType)?.label ??
                    attachment.documentType}
                  {' · chosen, not yet sent'}
                </span>
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRemove(attachment.id)}
                aria-label={`Remove ${attachment.file.name}`}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={onSkip}
        className="text-sm font-medium text-primary-600 dark:text-primary-400 underline hover:text-primary-700 dark:hover:text-primary-300"
      >
        Skip this step
      </button>
    </div>
  );
}

function ReviewStep({
  form,
  attachments,
  brokerName,
  capability,
  refusal,
  isSubmitting,
  onChooseBroker,
}: {
  form: JourneyState;
  attachments: JourneyAttachment[];
  brokerName: string;
  capability?: BrokerPricingCapability;
  refusal: { code?: string; detail?: string } | null;
  isSubmitting: boolean;
  onChooseBroker: () => void;
}) {
  const rows: [string, string][] = [
    ['Cover', titleCase(form.insuranceType)],
    ['Advisor', brokerName || 'Your advisor'],
    ['Insurers to ask', form.insurerNames.join(', ') || 'Advisor decides'],
    ['Plan', form.productName || 'No preference'],
    ['Name', form.proposerName],
    ['Mobile', form.proposerPhone],
    ['Email', form.proposerEmail || '—'],
    ['PIN code', form.proposerPincode || '—'],
    ...(form.insuranceType === 'motor'
      ? ([['Registration number', form.registrationNumber || '—']] as [string, string][])
      : ([['Cover amount', form.sumInsured ? formatCurrency(Number(form.sumInsured)) : '—']] as [
          string,
          string,
        ][])),
    [
      'Cover type',
      form.coverType
        ? (COVER_OPTIONS.find((option) => option.value === form.coverType)?.label ??
          titleCase(form.coverType))
        : 'Advisor recommends',
    ],
    ['Add-ons', form.addons.length ? form.addons.map((a) => titleCase(a)).join(', ') : 'None'],
    [
      'Documents',
      attachments.length
        ? attachments.map((a) => a.file.name).join(', ')
        : 'None (not required)',
    ],
  ];

  return (
    <div className="space-y-5">
      <dl className="divide-y divide-gray-100 dark:divide-gray-700">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4 py-2 text-sm">
            <dt className="text-gray-500">{label}</dt>
            <dd className="text-right font-medium text-gray-900 dark:text-white">{value}</dd>
          </div>
        ))}
      </dl>

      {/* STAGE 8, STATED BEFORE IT HAPPENS. Only 2 of ~26 brokers can price
          live; the other 24 must not be shown a spinner that implies one. */}
      {capability ? (
        <div
          className="rounded-lg border border-gray-200 p-4 text-sm dark:border-gray-700"
          data-testid="journey-pricing-path"
        >
          {capability.live_pricing ? (
            <p className="flex items-start gap-2 text-gray-700 dark:text-gray-200">
              <ClockIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-600" />
              <span>
                {brokerName} can fetch live premiums from{' '}
                {capability.insurers.map((i) => i.name).join(', ')}. We will ask them the moment
                you send this — and tell you exactly what came back.
              </span>
            </p>
          ) : (
            <p className="flex items-start gap-2 text-gray-700 dark:text-gray-200">
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
              <span>{capability.reason}</span>
            </p>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm dark:border-amber-900/40 dark:bg-amber-900/20">
          <p className="text-amber-800 dark:text-amber-200">
            Choose which of your advisors should handle this before sending.
          </p>
          <Button variant="outline" size="sm" className="mt-2" onClick={onChooseBroker}>
            Choose an advisor
          </Button>
        </div>
      )}

      {refusal?.code === NO_SERVER_REPLY ? (
        /* Amber, not red, and deliberately UNSURE. Three states: sent,
           refused, and "we do not know" -- which is what a timeout is. */
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm dark:border-amber-900/40 dark:bg-amber-900/20"
          role="alert"
        >
          <p className="font-medium text-amber-800 dark:text-amber-200">
            We did not hear back from the server
          </p>
          <p className="mt-1 text-amber-700 dark:text-amber-300">
            Your request may already have been sent. Check My Quotes before sending it again, so
            your advisor does not receive it twice.
          </p>
          <Link
            to="/my-quotes"
            className="mt-2 inline-block rounded-md border border-amber-300 px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100 dark:hover:bg-amber-900/30 dark:border-amber-700 dark:text-amber-100"
          >
            Check My Quotes
          </Link>
        </div>
      ) : refusal ? (
        <div
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm dark:border-red-900/40 dark:bg-red-900/20"
          role="alert"
        >
          <p className="font-medium text-red-800 dark:text-red-200">
            {refusal.code === BROKER_CHOICE_REQUIRED_CODE
              ? 'Choose which advisor should handle this'
              : 'Your request was not sent'}
          </p>
          <p className="mt-1 text-red-700 dark:text-red-300">{refusal.detail}</p>
        </div>
      ) : null}

      {isSubmitting && (
        <p className="text-sm text-gray-500 dark:text-gray-400" role="status">
          {attachments.length
            ? 'Sending your request and attaching your documents…'
            : 'Sending your request…'}
        </p>
      )}
    </div>
  );
}

function ResultStep({
  result,
  brokerName,
  outcomes,
  onRetryUploads,
  isRetrying,
}: {
  result: { id: string; requestNumber: string; pricing: JourneyPricingReport };
  brokerName: string;
  outcomes: JourneyAttachmentOutcome[];
  onRetryUploads: () => void;
  isRetrying: boolean;
}) {
  const { pricing } = result;
  const priced = pricing.status === 'priced' && pricing.options.length > 0;

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <CheckCircleIcon className="mt-0.5 h-6 w-6 flex-shrink-0 text-green-600" />
        <div>
          <p className="text-base font-semibold text-gray-900 dark:text-white">
            Request {result.requestNumber} is with {brokerName || 'your advisor'}
          </p>
          {/* The SERVER's own account of what happened, repeated verbatim.
              Never a hopeful sentence the UI made up. */}
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{pricing.detail}</p>
        </div>
      </div>

      {priced ? (
        <Card padding="none">
          <CardHeader title={`${pricing.options.length} premium(s) came back`} />
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {pricing.options.map((option) => (
              <li key={option.quote_id} className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-gray-900 dark:text-white">{option.insurer_name}</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(Number(option.total_premium))}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : (
        <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-200">
          {/* NOT a spinner and NOT an empty options table. Zero premiums is a
              fact, and it is stated. */}
          <p>
            No premium has been fetched for this request. {brokerName || 'Your advisor'} will price
            it and send you the options — they will appear under My Quotes.
          </p>
        </div>
      )}

      {/* STAGE 6, reported from what the SERVER said. An upload is listed as
          attached only when the response carried a document id; the request
          itself is filed either way, which is why a failure here is a line
          about the file and never "your request was not sent". */}
      {outcomes.length > 0 && (
        <div
          className="rounded-lg border border-gray-200 p-4 text-sm dark:border-gray-700"
          data-testid="journey-attachment-report"
        >
          <ul className="space-y-1">
            {outcomes.map((outcome) => (
              <li key={outcome.attachmentId} className="flex items-start gap-2">
                {outcome.status === 'uploaded' ? (
                  <CheckCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                ) : (
                  <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
                )}
                <span className="text-gray-700 dark:text-gray-200">
                  <span className="font-medium">{outcome.name}</span>
                  {outcome.status === 'uploaded'
                    ? ' — attached'
                    : ` — not attached. ${outcome.detail ?? ''}`}
                </span>
              </li>
            ))}
          </ul>
          {outcomes.some((outcome) => outcome.status === 'failed') && (
            <div className="mt-3">
              <p className="text-gray-600 dark:text-gray-300">
                Your request reached {brokerName || 'your advisor'} either way. You can try the
                files that did not attach again, or send them to your advisor directly.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={onRetryUploads}
                isLoading={isRetrying}
                disabled={isRetrying}
              >
                Try those files again
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <Link
          to={`/my-quotes/${result.id}`}
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          View this request
        </Link>
        <Link
          to="/my-quotes"
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:border-gray-600 dark:text-gray-200"
        >
          Back to My Quotes
        </Link>
      </div>
    </div>
  );
}
